import { useCallback } from 'react';

import { Analytics } from '@trello/atlassian-analytics';

import type { PanelNames } from './Panels';
import { useSplitScreenSharedState } from './useSplitScreenSharedState';

interface UseSplitScreenResizeReturn {
  collapsePanel: (panelName: PanelNames) => void;
  getAnalyticsAttributes: (
    panelName: PanelNames,
    newWidth: number,
    isToggled: boolean,
  ) => Record<string, unknown>;
  getMaxWidth: (panelName: PanelNames) => number;
  getMinWidth: (panelName: PanelNames) => number;
  sendResizeAnalyticsEvent: (panelName: PanelNames, newWidth: number) => void;
}

export const useSplitScreenResize = (): UseSplitScreenResizeReturn => {
  const { panels, togglePanel } = useSplitScreenSharedState({
    isDragToCollapseBoardEnabled: true,
  });

  const getAnalyticsAttributes = useCallback(
    (panelName: PanelNames, newWidth: number, isToggled: boolean) => {
      if (isToggled) {
        return {
          isInboxOpen: panelName !== 'inbox' && panels.inbox,
          isPlannerOpen: panelName !== 'planner' && panels.planner,
          isBoardOpen: panelName !== 'board' && panels.board,
        };
      }

      return {
        isInboxOpen: panels.inbox,
        isPlannerOpen: panels.planner,
        isBoardOpen: panels.board,
        newPanelSize: newWidth,
      };
    },
    [panels],
  );

  const sendResizeAnalyticsEvent = useCallback(
    (panelName: PanelNames, newWidth: number) => {
      if (panelName === 'switcher') return;

      Analytics.sendUIEvent({
        action: 'resized',
        actionSubject: 'panel',
        actionSubjectId: `${panelName}Panel`,
        source: 'personalProductivitySplitScreenResizingDivider',
        attributes: getAnalyticsAttributes(panelName, newWidth, false),
      });
    },
    [getAnalyticsAttributes],
  );

  const collapsePanel = useCallback(
    (panelName: PanelNames) => {
      if (panelName === 'switcher') return;
      togglePanel(panelName);

      Analytics.sendUIEvent({
        action: 'collapsed',
        actionSubject: 'panel',
        actionSubjectId: `${panelName}Panel`,
        source: 'personalProductivitySplitScreenResizingDivider',
        attributes: getAnalyticsAttributes(panelName, 0, true),
      });
    },
    [togglePanel, getAnalyticsAttributes],
  );

  const getMaxWidth = useCallback(
    (panelName: PanelNames) => {
      return panels.panelWidths[panelName].maxWidth;
    },
    [panels.panelWidths],
  );

  const getMinWidth = useCallback(
    (panelName: PanelNames) => {
      return panels.panelWidths[panelName].minWidth;
    },
    [panels.panelWidths],
  );

  return {
    collapsePanel,
    getMaxWidth,
    getMinWidth,
    getAnalyticsAttributes,
    sendResizeAnalyticsEvent,
  };
};
